import { useMemo, useState } from 'react';
import { Select } from '@customerio/pluma-components/react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useNewsletterCatalog, useNewsletterOptional } from '@/services/db/newsletter/queries';
import { isEmptyString } from '@/utils/string';
import { needsOffPageLookup, newsletterPickerOptions, type NewsletterOption } from './newsletter-recipients-options';

type NewsletterSelectProps = {
  /** The stored newsletter id (wire type preserved), or null when unpicked. */
  value: number | string | null;
  disabled?: boolean;
  /** The row-owned validation message. */
  error?: string;
  /** Fires with the picked option's value; the row owns the wire-type coercion. */
  onChange: (value: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
};

const SEARCH_DEBOUNCE_MS = 300;

/**
 * The picker's option list: the loaded pages of the (possibly searched)
 * catalog, with a stable entry for the stored selection when those pages
 * don't hold it. The off-page selection resolves its name through the by-id
 * lookup — usually served from the sideloaded detail cache — and a deleted
 * or inaccessible newsletter falls back to its raw id label, like the
 * legacy editor. `pickedName` keeps a pick made out of search results
 * labeled after the close resets the scope.
 */
function useNewsletterOptions(
  selectedValue: string | undefined,
  debouncedSearch: string,
  pickedNewsletter: NewsletterOption | null,
) {
  const workspaceId = requireWorkspaceId();
  const catalog = useNewsletterCatalog(workspaceId, isEmptyString(debouncedSearch) ? {} : { search: debouncedSearch });
  const loadedNewsletters = useMemo(
    () => (catalog.data?.pages ?? []).flatMap((page) => page.newsletters),
    [catalog.data],
  );

  const offPageNewsletter = useNewsletterOptional({
    workspaceId,
    id: selectedValue ?? '',
    enabled: catalog.isSuccess && needsOffPageLookup(selectedValue, loadedNewsletters, pickedNewsletter),
  });

  const options = useMemo(
    () => newsletterPickerOptions(loadedNewsletters, selectedValue, pickedNewsletter, offPageNewsletter.data),
    [loadedNewsletters, selectedValue, pickedNewsletter, offPageNewsletter.data],
  );

  return { options, catalog };
}

/**
 * The newsletter picker: server-paged (pulled on scroll) and server-searched
 * (case-insensitive name substring, debounced), with the legacy editor's
 * keep-stored-selection labeling (see {@link useNewsletterOptions}).
 */
export function NewsletterSelect({ value, disabled, error, onChange, onOpenChange }: NewsletterSelectProps) {
  const taxonomy = useProductTaxonomy();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // Keep the picked entry: a close resets the search, and the reset first
  // page may not contain a newsletter picked out of search results.
  const [pickedNewsletter, setPickedNewsletter] = useState<NewsletterOption | null>(null);

  const selectedValue = value == null ? undefined : String(value);
  const { options, catalog } = useNewsletterOptions(selectedValue, debouncedSearch, pickedNewsletter);

  const handleChange = (picked: string | null) => {
    if (picked == null) {
      return;
    }
    const pickedOption = options.find((option) => option.value === picked);
    if (pickedOption != null) {
      setPickedNewsletter(pickedOption);
    }
    onChange(picked);
  };

  const handleOpenChange = (isOpen: boolean) => {
    // The term externally controls the select's input, so a leftover one
    // silently filters the list on reopen (FPS-3558). Resetting the scope
    // also supersedes any in-flight load-more of the dismissed search — its
    // pages land in that scope's cache slot, not the reset list.
    if (!isOpen && !isEmptyString(searchInput)) {
      setSearchInput('');
    }
    onOpenChange?.(isOpen);
  };

  return (
    <Select
      data-testid="dryad-newsletter-select"
      ariaLabel={taxonomy.term('broadcasts.titleSingular')}
      size="sm"
      placeholder={taxonomy.term('broadcasts.titlePlural')}
      searchPlaceholder={`Search by ${taxonomy.term('broadcasts.singular')}…`}
      options={options}
      value={selectedValue}
      error={error}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      isSearchable
      searchInputValue={searchInput}
      onSearchInput={(event) => setSearchInput(event.target.value)}
      isLoading={(catalog.isFetching && !catalog.isFetchingNextPage) || searchInput !== debouncedSearch}
      onLoadMore={() => {
        void catalog.fetchNextPage();
      }}
      shouldLoadMore={catalog.hasNextPage}
      isLoadingMore={catalog.isFetchingNextPage}
      isDisabled={disabled}
    />
  );
}
